import { createRouter, createWebHistory } from "vue-router"; // 改为history模式去掉#
import { ElNotification } from "element-plus";
import config from "@/config";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import tool from "@/utils/tool";
import systemRouter from "./systemRouter";
import { beforeEach, afterEach } from "./scrollBehavior";

// Webpack环境下批量导入views目录下的所有.vue文件
const requireViews = require.context('../components', true, /\.vue$/);

// 系统特殊路由组件
const otherModules = {
    404: () => import("../views/404.vue"),
    empty: () => import("../views/Empty.vue"),
};

// 系统路由
const routes = systemRouter;

// 404路由配置
const routes_404 = {
    path: "/:pathMatch(.*)*",
    hidden: true,
    component: otherModules["404"],
};
let routes_404_r = () => {};

// 创建路由实例，使用history模式去掉URL中的#
const router = createRouter({
    history: createWebHistory(),
    routes,
});

// 判断是否已加载过动态/静态路由
let isGetRouter = false;

router.beforeEach(async (to, from, next) => {
    NProgress.start();
    // 动态标题
    document.title = `${to.meta.title || '首页'} - 纵横云创官网平台`;

    const token = tool.data.get(config.TOKEN);

    // 处理特殊路由
    if (['/indexRoute', '/onlyOfficePreview', '/login'].includes(to.path)) {
        // 重置路由状态
        if (routes[0]) {
            router.addRoute(routes[0]);
        }
        routes_404_r(); // 移除404路由
        isGetRouter = false;
        next();
        return;
    }

    // 未登录处理
    if (!token && to.path !== '/') {
        next({ path: "/" });
        return;
    }

    // 整页路由处理
    if (to.meta.fullpage && to.matched.length > 0) {
        to.matched = [to.matched[to.matched.length - 1]];
    }

    // 加载动态/静态路由
    if (!isGetRouter) {
        try {
            const apiMenu = tool.data.get("MENU") || [];
            let menuRouter = filterAsyncRouter(apiMenu);
            menuRouter = flatAsyncRoutes(menuRouter);

            // 添加路由
            menuRouter.forEach((item) => {
                router.addRoute("layout", item);
            });

            // 添加404路由
            routes_404_r = router.addRoute(routes_404);

            // 如果当前路由未匹配，重定向到当前路径以激活新路由
            if (to.matched.length === 0) {
                return next(to.fullPath);
            }

            isGetRouter = true;
        } catch (error) {
            ElNotification.error({
                title: "路由加载错误",
                message: error.message,
            });
            return next('/login');
        }
    }

    beforeEach(to, from);
    next();
});

router.afterEach((to, from) => {
    afterEach(to, from);
    NProgress.done();
});

router.onError((error) => {
    NProgress.done();
    ElNotification.error({
        title: "路由错误",
        message: error.message || "路由加载失败",
    });
});

// 自定义方法：获取菜单
router.getMenu = () => {
    return tool.data.get("MENU") || [];
};

// 转换菜单为路由配置
function filterAsyncRouter(routerMap) {
    return routerMap.map((item) => {
        const meta = { ...item.meta };
        let path = item.path || '';
        let component = item.path;

        // 处理外部链接特殊路由
        if (meta.target === "iframe") {
            meta.url = path;
            path = `/i/${meta.id || Date.now()}`; // 确保路径唯一
            component = undefined;
        }

        // 构建路由对象
        const route = {
            path: path,
            name: `${path.replace(/[^\w]/g, '-')}-${meta.id || ''}`, // 生成唯一name
            meta: meta,
            redirect: item.redirect || undefined,
            children: item.children && item.children.length ? filterAsyncRouter(item.children) : undefined,
            component: loadComponent(component),
        };

        return route;
    });
}

// 加载组件（适配Webpack）
function loadComponent(componentPath) {
    if (!componentPath) {
        return otherModules["empty"];
    }

    try {
        // 查找匹配的组件
        const matchedPath = requireViews.keys().find(key => {
            const normalizedKey = key.replace('./', '/');
            return normalizedKey === `${componentPath}.vue` ||
                normalizedKey === `${componentPath}/index.vue`;
        });

        if (matchedPath) {
            return () => Promise.resolve(requireViews(matchedPath).default);
        }
    } catch (error) {
        console.error(`加载组件失败: ${componentPath}`, error);
    }

    return otherModules["empty"];
}

// 路由扁平化处理
function flatAsyncRoutes(routes, breadcrumb = []) {
    const res = [];

    routes.forEach((route) => {
        const tmp = { ...route };
        const currentBreadcrumb = [...breadcrumb, tmp];

        // 如果有子路由，先处理当前路由再递归处理子路由
        if (tmp.children && tmp.children.length > 0) {
            // 添加当前路由（不含子路由）
            const currentRoute = { ...tmp };
            currentRoute.meta = { ...currentRoute.meta, breadcrumb: currentBreadcrumb };
            delete currentRoute.children;
            if (currentRoute.path) {
                res.push(currentRoute);
            }

            // 递归处理子路由
            const childrenRoutes = flatAsyncRoutes(tmp.children, currentBreadcrumb);
            res.push(...childrenRoutes);
        } else {
            // 没有子路由，直接添加
            tmp.meta = { ...tmp.meta, breadcrumb: currentBreadcrumb };
            if (tmp.path) {
                res.push(tmp);
            }
        }
    });

    return res;
}

export default router;
